import { useCallback, useEffect, useState } from 'react';
import { FRIENDLY_DEPENDENCY_NAMES } from '../../../constants/dependency';
import { SYSTEM_TYPE_IDS, SYSTEM_VALUE_IDS } from '../../../constants/system';
import translations from '../../../translations';
import type { ElementAPI } from '../../../types/element';
import ButtonDefault from '../../buttons/ButtonDefault';
import Table, { type TableColumnList } from '../../generic/Table';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import SearchImportExisting from './SearchImportExisting';

const SYSTEM_VALUE_AND_TYPE_IDS = SYSTEM_VALUE_IDS.concat(SYSTEM_TYPE_IDS);

type Props = {
    onFetch: () => Promise<ElementAPI[]>;
    elementType: string;
    importElement: (id: string) => void;
    isOpen: boolean;
};

const ImportedElements = ({ elementType, importElement, isOpen, onFetch }: Props) => {
    const [tableData, setTableData] = useState<ElementAPI[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    const excludeSummaryRow = elementType === FRIENDLY_DEPENDENCY_NAMES.VALUE_ELEMENT;

    const tableColumns = () => {
        const columns: TableColumnList<ElementAPI> = [];

        columns.push({
            renderHeader: () => translations.COMMON_TABLE_name,
            renderCell: ({ item }) => item.developerName,
        });

        if (!excludeSummaryRow) {
            columns.push({
                renderHeader: () => translations.COMMON_TABLE_summary,
                renderCell: ({ item }) => item.developerSummary,
            });
        }

        columns.push({
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <div className="action-btn-wrapper">
                    <ButtonDefault
                        title={`Import ${item.developerName}`}
                        className="btn-sm"
                        aria-label={`Import ${item.developerName}`}
                        onClick={() => importElement(item.id)}
                    >
                        Import
                    </ButtonDefault>
                </div>
            ),
            size: '6rem',
        });

        return columns;
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    const getValueElements = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await onFetch();
            setTableData(filterElements(response));
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        } finally {
            setIsLoading(false);
        }
    }, [dispatchNotification, onFetch]);

    const filterElements = (newData: ElementAPI[]) => {
        return newData
            .filter((item) => !SYSTEM_VALUE_AND_TYPE_IDS.includes(item.id))
            .filter((item) => {
                return tableData.every((filter) => {
                    return filter.id !== item.id;
                });
            });
    };

    const filteredTableData = tableData.filter((row) => {
        const name = row.developerName ? row.developerName.toLowerCase() : '';
        return name.includes(searchQuery.toLowerCase());
    });

    useEffect(() => {
        if (isOpen) {
            getValueElements();
        }
    }, [getValueElements, isOpen]);

    if (!isOpen) {
        return null;
    }

    if (isLoading) {
        return <Loader message="Loading data..." />;
    }

    return (
        <>
            <SearchImportExisting setSearchQuery={setSearchQuery} getElements={getValueElements} />
            <Table
                items={filteredTableData}
                columns={tableColumns()}
                pagination={true}
                testId={'shared-elements-import'}
            />
        </>
    );
};

export default ImportedElements;
